import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { serviceCategoriesApi, servicesApi } from './servicesApi';
import type {
  ServiceCategoryListParams,
  ServiceCategoryPayload,
  ServiceListParams,
  ServicePayload,
  ServiceUpdatePayload,
} from './types';

const SERVICES_KEY = 'services';
const CATEGORIES_KEY = 'serviceCategories';

/**
 * Invalidates both halves of the catalogue after any service write.
 *
 * A category carries the number of services filed under it, so moving one
 * between groups — or retiring it — leaves the category list stale even though
 * no category row changed. The two are never independently valid, and treating
 * them as one cache saves a class of "the count says 3, I can see 2" bug.
 */
const useInvalidateCatalogue = () => {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [SERVICES_KEY] });
    void qc.invalidateQueries({ queryKey: [CATEGORIES_KEY] });
  };
};

export const useServices = (params?: ServiceListParams) =>
  useQuery({
    queryKey: [SERVICES_KEY, params],
    queryFn: () => servicesApi.list(params),
  });

export const useServiceCategories = (params?: ServiceCategoryListParams) =>
  useQuery({
    queryKey: [CATEGORIES_KEY, params],
    queryFn: () => serviceCategoriesApi.list(params),
  });

export const useCreateService = () => {
  const invalidate = useInvalidateCatalogue();
  return useMutation({
    mutationFn: (data: ServicePayload) => servicesApi.create(data),
    onSuccess: invalidate,
  });
};

export const useUpdateService = () => {
  const qc = useQueryClient();
  const invalidate = useInvalidateCatalogue();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ServiceUpdatePayload }) =>
      servicesApi.update(id, data),
    onSuccess: (_result, variables) => {
      invalidate();
      // The update may have opened a new price period, so an open history
      // modal is showing a timeline that is one entry short.
      void qc.invalidateQueries({ queryKey: [SERVICES_KEY, 'priceHistory', variables.id] });
    },
  });
};

export const useDeleteService = () => {
  const invalidate = useInvalidateCatalogue();
  return useMutation({
    mutationFn: (id: number) => servicesApi.remove(id),
    onSuccess: invalidate,
  });
};

export const useCreateCategory = () => {
  const invalidate = useInvalidateCatalogue();
  return useMutation({
    mutationFn: (data: ServiceCategoryPayload) => serviceCategoriesApi.create(data),
    onSuccess: invalidate,
  });
};

export const useUpdateCategory = () => {
  const invalidate = useInvalidateCatalogue();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ServiceCategoryPayload }) =>
      serviceCategoriesApi.update(id, data),
    onSuccess: invalidate,
  });
};

export const useDeleteCategory = () => {
  const invalidate = useInvalidateCatalogue();
  return useMutation({
    mutationFn: (id: number) => serviceCategoriesApi.remove(id),
    onSuccess: invalidate,
  });
};

/** The price timeline of one service. Fetched only while the modal is open. */
export const usePriceHistory = (serviceId: number | null) =>
  useQuery({
    queryKey: [SERVICES_KEY, 'priceHistory', serviceId],
    queryFn: () => servicesApi.priceHistory(serviceId as number),
    enabled: serviceId !== null && Number.isFinite(serviceId) && serviceId > 0,
  });
